/* eslint-disable @typescript-eslint/no-explicit-any */

// === ТИПЫ МЕТАБУЛЛИТОВ ===

// Типы текстовых сущностей
export type TextEntityType =
  | 'paragraph'
  | 'title'
  | 'subtitle'
  | 'heading-1'
  | 'heading-2'
  | 'heading-3'
  | 'bullet-level-1'
  | 'bullet-level-2'
  | 'bullet-level-3'
  | 'bullet-level-4'
  | 'bullet-level-5';

/**
 * Опции для создания TextEntity объекта
 * Расширяет базовые опции Fabric.js с кастомными полями метабуллитов
 */
export interface TextEntityOptions {
  // Основной идентификатор
  id?: string;

  // Тип текстовой сущности
  entityType?: TextEntityType;

  // Уникальный ID метабуллита (группирует связанные сущности)
  metaBulletId?: string;

  // Порядковый номер в рамках метабуллита
  entityOrder?: number;

  // Уровень вложенности буллита (1-5, только для bullet-level-*)
  bulletLevel?: number;

  // Флаг что это сущность метабуллита
  isMetaBulletEntity?: boolean;

  // Дополнительные отступы
  marginBottom?: number;
  marginLeft?: number;

  // Исключить из бэкапа
  excludeFromBackup?: boolean;

  // Базовые опции Fabric.js
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string | number;
  fill?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  lineHeight?: number;
  visible?: boolean;
  selectable?: boolean;
  evented?: boolean;
  opacity?: number;
  angle?: number;
  scaleX?: number;
  scaleY?: number;
}

// === КОНФИГУРАЦИЯ СТИЛЕЙ ===

// Дефолтные стили для типов сущностей
export interface TextEntityStyleConfig {
  fontSize: number;
  fontFamily: string;
  fontWeight: string | number;
  lineHeight: number;
  marginBottom: number;
  marginLeft: number;
  textAlign: string;
  fill: string; // цвет текста
}

// Конфигурация стилей для всех типов
export interface MetaBulletStylesConfig {
  paragraph: TextEntityStyleConfig;
  title: TextEntityStyleConfig;
  subtitle: TextEntityStyleConfig;
  'heading-1': TextEntityStyleConfig;
  'heading-2': TextEntityStyleConfig;
  'heading-3': TextEntityStyleConfig;
  'bullet-level-1': TextEntityStyleConfig;
  'bullet-level-2': TextEntityStyleConfig;
  'bullet-level-3': TextEntityStyleConfig;
  'bullet-level-4': TextEntityStyleConfig;
  'bullet-level-5': TextEntityStyleConfig;
}

// === СОБЫТИЯ МЕТАБУЛЛИТОВ ===

// События метабуллитов
export interface MetaBulletEvent {
  entityId: string;
  metaBulletId: string;
  eventType: 'text_changed' | 'type_changed' | 'level_changed' | 'position_changed';
  data?: unknown;
}

// === УТИЛИТАРНЫЕ ТИПЫ ===

// Утилитарные типы для работы с буллитами
export type BulletType = Extract<TextEntityType, `bullet-level-${number}`>;
export type NonBulletType = Exclude<TextEntityType, BulletType>;

// === КОНСТАНТЫ И МАППИНГИ ===

// Маппинг shortcuts к типам сущностей для slash-команд
export const ENTITY_TYPE_SHORTCUTS: Record<string, TextEntityType> = {
  // Основные типы
  p: 'paragraph',
  paragraph: 'paragraph',

  // Заголовки
  title: 'title',
  t: 'title',

  subtitle: 'subtitle',
  sub: 'subtitle',

  h1: 'heading-1',
  heading1: 'heading-1',
  '#': 'heading-1',

  h2: 'heading-2',
  heading2: 'heading-2',
  '##': 'heading-2',

  h3: 'heading-3',
  heading3: 'heading-3',
  '###': 'heading-3',

  // Буллиты
  bullet: 'bullet-level-1',
  bullet1: 'bullet-level-1',
  b1: 'bullet-level-1',

  bullet2: 'bullet-level-2',
  b2: 'bullet-level-2',

  bullet3: 'bullet-level-3',
  b3: 'bullet-level-3',

  bullet4: 'bullet-level-4',
  b4: 'bullet-level-4',

  bullet5: 'bullet-level-5',
  b5: 'bullet-level-5',
};

// Обратная мапка - от типа к основному shortcut
export const TYPE_TO_SHORTCUT: Record<TextEntityType, string> = {
  paragraph: 'p',
  title: 'title',
  subtitle: 'sub',
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'bullet-level-1': 'bullet',
  'bullet-level-2': 'bullet2',
  'bullet-level-3': 'bullet3',
  'bullet-level-4': 'bullet4',
  'bullet-level-5': 'bullet5',
};

// Клавиатурные команды для буллитов (отдельно от slash-команд)
export const BULLET_KEYBOARD_COMMANDS = {
  TAB: 'increase_bullet_level', // Tab - увеличить уровень буллита
  SHIFT_TAB: 'decrease_bullet_level', // Shift+Tab - уменьшить уровень буллита
  ENTER: 'create_new_bullet', // Enter - создать новый буллит того же уровня
} as const;

// === УТИЛИТАРНЫЕ ФУНКЦИИ ===

// Утилитарные функции для работы с типами
export function isBulletType(type: TextEntityType): type is BulletType {
  return type.startsWith('bullet-level-');
}

export function getBulletLevel(type: BulletType): number {
  const match = type.match(/bullet-level-(\d+)/);
  return match ? parseInt(match[1], 10) : 1;
}

export function getBulletTypeForLevel(level: number): BulletType {
  return `bullet-level-${Math.max(1, Math.min(5, level))}` as BulletType;
}

// Утилитарные функции для работы с shortcuts
export function getEntityTypeByShortcut(shortcut: string): TextEntityType | null {
  const normalizedShortcut = shortcut.toLowerCase().trim();
  return ENTITY_TYPE_SHORTCUTS[normalizedShortcut] || null;
}

export function getShortcutByEntityType(entityType: TextEntityType): string {
  return TYPE_TO_SHORTCUT[entityType] || '';
}

// Получить все доступные shortcuts для типа
export function getAllShortcutsForType(entityType: TextEntityType): string[] {
  return Object.entries(ENTITY_TYPE_SHORTCUTS)
    .filter(([, type]) => type === entityType)
    .map(([shortcut]) => shortcut);
}

// Поиск частичного совпадения для автокомплита
export function findShortcutMatches(
  input: string,
): Array<{ shortcut: string; entityType: TextEntityType }> {
  const normalizedInput = input.toLowerCase().trim();

  return Object.entries(ENTITY_TYPE_SHORTCUTS)
    .filter(([shortcut]) => shortcut.startsWith(normalizedInput))
    .map(([shortcut, entityType]) => ({ shortcut, entityType }))
    .sort((a, b) => a.shortcut.length - b.shortcut.length); // Сначала более короткие
}
